import React, { useState, useEffect } from 'react';
import { clinicAPI } from '../../services/api/adminAPI';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';
import { 
  Plus, 
  Building2, 
  Users, 
  Activity, 
  Calendar,
  Edit2,
  Trash2,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle
} from 'lucide-react';
import toast from 'react-hot-toast';

const ClinicManagement = () => {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    pageSize: 10
  });
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedClinic, setSelectedClinic] = useState(null);
  const [formData, setFormData] = useState({ name: '', status: 'active' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadClinics();
  }, [pagination.currentPage]);

  const loadClinics = async () => {
    try {
      setLoading(true);
      const skip = (pagination.currentPage - 1) * pagination.pageSize;
      const response = await clinicAPI.getAllClinics(skip, pagination.pageSize);
      
      setClinics(response.data.clinics);
      
      // Update pagination if backend provides total count
      if (response.data.total_count !== undefined) {
        setPagination(prev => ({
          ...prev,
          totalCount: response.data.total_count,
          totalPages: Math.ceil(response.data.total_count / pagination.pageSize)
        }));
      }
    } catch (error) {
      toast.error('Failed to load clinics');
      console.error('Error loading clinics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClinic = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Clinic name is required');
      return;
    }

    setSubmitting(true);
    try {
      await clinicAPI.createClinic({ name: formData.name });
      toast.success('Clinic created successfully!');
      setShowAddModal(false);
      setFormData({ name: '', status: 'active' });
      loadClinics();
    } catch (error) {
      toast.error('Failed to create clinic');
      console.error('Error creating clinic:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClinic = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Clinic name is required');
      return;
    }

    setSubmitting(true);
    try {
      await clinicAPI.updateClinic(selectedClinic.id, formData);
      toast.success('Clinic updated successfully!');
      setShowEditModal(false);
      setSelectedClinic(null);
      setFormData({ name: '', status: 'active' });
      loadClinics();
    } catch (error) {
      toast.error('Failed to update clinic');
      console.error('Error updating clinic:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClinic = async (clinic) => {
    if (!window.confirm(`Are you sure you want to deactivate "${clinic.name}"? This will also deactivate all users in this clinic.`)) {
      return;
    }

    try {
      await clinicAPI.deleteClinic(clinic.id);
      toast.success('Clinic deactivated successfully');
      loadClinics();
    } catch (error) {
      toast.error('Failed to deactivate clinic');
      console.error('Error deleting clinic:', error);
    }
  };

  const openEditModal = (clinic) => {
    setSelectedClinic(clinic);
    setFormData({ name: clinic.name, status: clinic.status });
    setShowEditModal(true);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'inactive':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'suspended':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      default:
        return <XCircle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-red-100 text-red-800',
      suspended: 'bg-yellow-100 text-yellow-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badges[status] || 'bg-gray-100 text-gray-800'}`}>
        {getStatusIcon(status)}
        <span className="ml-1 capitalize">{status}</span>
      </span>
    );
  };

  const columns = [
    {
      header: 'Clinic',
      accessor: 'name',
      render: (clinic) => (
        <div className="flex items-center">
          <div className="p-2 bg-blue-100 rounded-lg mr-3">
            <Building2 className="h-5 w-5 text-blue-600" />
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">{clinic.name}</div>
            <div className="text-sm text-gray-500">ID: {clinic.id.substring(0, 8)}...</div>
          </div>
        </div>
      )
    },
    {
      header: 'Status',
      accessor: 'status',
      render: (clinic) => getStatusBadge(clinic.status)
    },
    {
      header: 'Users',
      accessor: 'statistics.total_users',
      render: (clinic) => (
        <div className="flex items-center">
          <Users className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900">
            {clinic.statistics?.active_users || 0} / {clinic.statistics?.total_users || 0}
          </span>
        </div>
      )
    },
    {
      header: 'Patients',
      accessor: 'statistics.total_patients',
      render: (clinic) => (
        <div className="flex items-center">
          <Users className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900">{clinic.statistics?.total_patients || 0}</span>
        </div>
      )
    },
    {
      header: 'Recordings',
      accessor: 'statistics.total_recordings',
      render: (clinic) => (
        <div className="flex items-center">
          <Activity className="h-4 w-4 text-gray-400 mr-1" />
          <span className="text-sm text-gray-900">{clinic.statistics?.total_recordings || 0}</span>
        </div>
      )
    },
    {
      header: 'Created',
      accessor: 'created_at',
      render: (clinic) => (
        <div className="text-sm text-gray-500">
          {new Date(clinic.created_at).toLocaleDateString()}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: 'actions',
      render: (clinic) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={() => openEditModal(clinic)}
            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
            title="Edit Clinic"
          >
            <Edit2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteClinic(clinic)}
            className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
            title="Deactivate Clinic"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )
    }
  ];

  const filteredClinics = clinics.filter(clinic =>
    clinic.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clinic Management</h2>
          <p className="text-gray-600 mt-1">Manage all clinics and their configurations</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="mt-4 sm:mt-0 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Clinic
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-blue-100">
              <Building2 className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">{clinics.length}</h3>
              <p className="text-gray-600 text-sm">Total Clinics</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-green-100">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {clinics.filter(c => c.status === 'active').length}
              </h3>
              <p className="text-gray-600 text-sm">Active Clinics</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-purple-100">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {clinics.reduce((sum, clinic) => sum + (clinic.statistics?.total_users || 0), 0)}
              </h3>
              <p className="text-gray-600 text-sm">Total Users</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="p-3 rounded-full bg-orange-100">
              <Activity className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {clinics.reduce((sum, clinic) => sum + (clinic.statistics?.total_recordings || 0), 0)}
              </h3>
              <p className="text-gray-600 text-sm">Total Recordings</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Table */}
      <DataTable
        data={filteredClinics}
        columns={columns}
        loading={loading}
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        emptyMessage="No clinics found"
        pagination={pagination}
        onPageChange={(page) => setPagination(prev => ({ ...prev, currentPage: page }))}
      />

      {/* Add Clinic Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Clinic"
      >
        <form onSubmit={handleAddClinic} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinic Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter clinic name"
              required
            />
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowAddModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating...
                </>
              ) : (
                'Create Clinic'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit Clinic Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Clinic"
      >
        <form onSubmit={handleEditClinic} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Clinic Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter clinic name"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="suspended">Suspended</option>
            </select>
          </div>
          
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={() => setShowEditModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {submitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Updating...
                </>
              ) : (
                'Update Clinic'
              )}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ClinicManagement;